/**
 * Mobile card carousel for the sl-* card grids (UGC clips, review cards).
 *
 * The sideways layout itself is pure CSS scroll-snap — this script only adds
 * what CSS cannot:
 *  - a dot pager, so the length of the row is visible at a glance;
 *  - an "is-active" mark on the snapped card, which fades and shrinks its
 *    neighbours so one card reads as the one being looked at;
 *  - a single nudge the first time the row scrolls into view, because an older
 *    visitor who has never met a swipe row needs to SEE it move once.
 *
 * Cards keep full strength until the wrapper is marked ready, so a page with
 * JavaScript off still shows every card at full contrast.
 *
 * Sections include this file themselves (so each one works on any page); the
 * flag below stops the second and third copy from running it again.
 */
(function () {
  'use strict';

  if (window.slMobileCarouselLoaded) return;
  window.slMobileCarouselLoaded = true;

  var MOBILE = '(max-width: 749px)';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setup(wrap) {
    if (wrap.dataset.slMcarBound) return;

    var track = wrap.querySelector('[data-sl-mcar-track]');
    if (!track) return;

    var cards = Array.prototype.slice.call(track.children);
    if (cards.length < 2) return;

    wrap.dataset.slMcarBound = '1';

    var dotsHost = wrap.querySelector('[data-sl-mcar-dots]');
    var dots = [];
    var active = -1;
    var ticking = false;

    function padLeft() {
      return parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    }

    function scrollToCard(index) {
      var delta =
        cards[index].getBoundingClientRect().left -
        track.getBoundingClientRect().left -
        padLeft();
      track.scrollBy({ left: delta, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    if (dotsHost) {
      cards.forEach(function (card, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sl-mcar__dot';
        dot.setAttribute('aria-label', 'Show item ' + (i + 1) + ' of ' + cards.length);
        dot.innerHTML = '<span class="sl-mcar__dot-i"></span>';
        dot.addEventListener('click', function () {
          scrollToCard(i);
        });
        dotsHost.appendChild(dot);
        dots.push(dot);
      });
    }

    // The snapped card is the one whose centre sits closest to the track centre.
    function currentIndex() {
      var trackBox = track.getBoundingClientRect();
      var centre = trackBox.left + trackBox.width / 2;
      var best = 0;
      var bestGap = Infinity;

      for (var i = 0; i < cards.length; i++) {
        var box = cards[i].getBoundingClientRect();
        var gap = Math.abs(box.left + box.width / 2 - centre);
        if (gap < bestGap) {
          bestGap = gap;
          best = i;
        }
      }
      return best;
    }

    function sync() {
      var index = currentIndex();
      if (index === active) return;

      if (cards[active]) cards[active].classList.remove('is-active');
      if (dots[active]) {
        dots[active].classList.remove('is-active');
        dots[active].removeAttribute('aria-current');
      }

      active = index;
      cards[active].classList.add('is-active');
      if (dots[active]) {
        dots[active].classList.add('is-active');
        dots[active].setAttribute('aria-current', 'true');
      }
    }

    track.addEventListener(
      'scroll',
      function () {
        if (track.scrollLeft > 8) wrap.classList.add('sl-mcar--used');
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', sync, { passive: true });

    // One-time "this row moves" hint, fired when the row first comes into view.
    function nudge() {
      if (!window.matchMedia(MOBILE).matches) return;
      if (reducedMotion() || track.scrollLeft > 4) return;
      wrap.classList.add('sl-mcar--nudge');
      window.setTimeout(function () {
        wrap.classList.remove('sl-mcar--nudge');
      }, 1400);
    }

    if ('IntersectionObserver' in window) {
      var seen = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            seen.disconnect();
            nudge();
          });
        },
        { threshold: 0.4 }
      );
      seen.observe(track);
    }

    wrap.classList.add('sl-mcar--ready');
    sync();
  }

  function register(root) {
    (root || document).querySelectorAll('[data-sl-mcar]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      register();
    });
  } else {
    register();
  }

  // Sections re-render without a page load inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    register(event.target);
  });
})();
